"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ElectricalInterfaceCompatibilityAssertions = exports.PinOffBoardConnection = exports.PinExternalConnectionsMatchSpec = exports.PinFloatMatchesSpec = exports.OutputSingleSidedDriverBiased = exports.OutputNoConflictingDriver = exports.InputAnalogDrivenValid = exports.InputReachesFunctionalState = exports.InputNoIndeterminateState = exports.InputVilMet = exports.InputVihMet = exports.ElectricalInterfaceCompatibilityRequirement = void 0;
const enums_1 = require("../enums");
const ELECTRICAL_INTERFACE_REQUIREMENT_ID = 'req_cadstromeif0000000000000000000000000';
exports.ElectricalInterfaceCompatibilityRequirement = {
    id: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    slug: 'cadstrom.eif',
    slugPrefix: 'EIF',
    name: 'Electrical Interface Compatibility',
    description: 'Asserts that every pin presents a compatible electrical interface to whatever it connects to — each driven input reaches a valid level with robust margin against device min/max, no net is left contended or indeterminate, single-sided drivers are biased to their passive level, no pin is improperly floating, and off-board pins are compatible with the external interface they mate. Evaluated on a pin-by-pin basis.',
};
exports.InputVihMet = {
    id: 'asrt_eifinpvihmet000000000000000000000000',
    slug: 'cadstrom.eif.input.vih_met',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Digital inputs must be driven such that VIH is met',
    description: 'A signal that effectively drives a digital input must meet that input’s VIH, whether driven dynamically (e.g. by a GPO) or statically (e.g. biased high through a resistor). Resolved Not Applicable for inputs statically biased low.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.InputVilMet = {
    id: 'asrt_eifinpvilmet000000000000000000000000',
    slug: 'cadstrom.eif.input.vil_met',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Digital inputs must be driven such that VIL is met',
    description: 'A signal that effectively drives a digital input must meet that input’s VIL, whether driven dynamically or statically (e.g. biased low through a resistor). Resolved Not Applicable for inputs statically biased high.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.InputNoIndeterminateState = {
    id: 'asrt_eifinpnoindet00000000000000000000000',
    slug: 'cadstrom.eif.input.no_indeterminate_state',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Digital inputs must not be left in unpermitted indeterminate states',
    description: 'A digital input must never be able to settle to a voltage between its VIL and VIH (the indeterminate region) unless indeterminate operation has been explicitly permitted for that input.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.InputReachesFunctionalState = {
    id: 'asrt_eifinpreachfn00000000000000000000000',
    slug: 'cadstrom.eif.input.reaches_functional_state',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Digital inputs must be able to reach their nominally functional state',
    description: 'Given the drivers and bias network present on its net, a digital input must be able to attain the logic state required for the component to function nominally. Resolved Not Applicable for inputs with no defined nominally functional state.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.InputAnalogDrivenValid = {
    id: 'asrt_eifinpanalogdrv000000000000000000000',
    slug: 'cadstrom.eif.input.analog_driven_valid',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Digital inputs driven by analog outputs must be functionally valid',
    description: 'Where an analog output drives a digital input, the analog signal must be able to resolve to valid logic levels at that input — its swing must reach both a valid logic HIGH (≥ VIH) and LOW (≤ VIL). A digital output driving an analog input is not evaluated here.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.OutputNoConflictingDriver = {
    id: 'asrt_eifoutnoconflict00000000000000000000',
    slug: 'cadstrom.eif.output.no_conflicting_driver',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Digital outputs must not have a conflicting driver',
    description: 'A net driven by a digital output must not be simultaneously driven by another output to a conflicting logic level (e.g. two push-pull outputs, or a mix of high- and low-side single-sided drivers).',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.OutputSingleSidedDriverBiased = {
    id: 'asrt_eifoutsinglesided0000000000000000000',
    slug: 'cadstrom.eif.output.single_sided_driver_biased',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Single-sided drivers must be correctly biased',
    description: 'A single-sided driver — one that can actively drive only one logic level — must have the bias network needed to reach its passive level: an open-drain output needs a pull-up to a known supply meeting receiver VIH; an open-source output needs a pull-down meeting receiver VIL.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.PinFloatMatchesSpec = {
    id: 'asrt_eifpinfloatspec000000000000000000000',
    slug: 'cadstrom.eif.pin.float_matches_spec',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Pin floating behavior must match specification',
    description: 'A pin’s floating state must match its specification in both directions. A pin must not be left floating — connected to no driver and no bias source — unless floating is permitted for it (e.g. a documented no-connect); conversely, a pin that is required to float — e.g. a reserved or do-not-connect pin — must have no driver, bias, or connection to any other pin, since driving or biasing it can violate the device’s requirements. Pins with internal bias resistors are generally not considered floating.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.PinExternalConnectionsMatchSpec = {
    id: 'asrt_eifpinextconnspec0000000000000000000',
    slug: 'cadstrom.eif.pin.external_connections_match_spec',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Pin external connections must match specification',
    description: 'Every external connection specified for a pin must be present on the design. A connection is either direct — the pin strapped to another pin or rail, e.g. EN to VIN — or through a component such as a pull-up or pull-down resistor or a decoupling capacitor; the component must be of the specified kind, fitted, and within the specified value. A pin with several specified connections is judged on all of them together; a missing or incorrect connection leaves the pin without the bias, decoupling, or configuration the device requires.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.PinOffBoardConnection = {
    id: 'asrt_eifpinoffboard0000000000000000000000',
    slug: 'cadstrom.eif.pin.off_board_connection',
    requirementDefinitionId: ELECTRICAL_INTERFACE_REQUIREMENT_ID,
    name: 'Pins connected to external connectors must be functionally compatible with the external system',
    description: 'A pin leaving the board through a connector must be functionally compatible with the external system it mates with. Compatibility can only be confirmed for known connector types whose interface is defined (e.g. USB); otherwise the external interface should be verified by hand.',
    lifecycle: enums_1.AssertionDefinitionLifecycle.Active,
};
exports.ElectricalInterfaceCompatibilityAssertions = [
    exports.InputVihMet,
    exports.InputVilMet,
    exports.InputNoIndeterminateState,
    exports.InputReachesFunctionalState,
    exports.InputAnalogDrivenValid,
    exports.OutputNoConflictingDriver,
    exports.OutputSingleSidedDriverBiased,
    exports.PinFloatMatchesSpec,
    exports.PinExternalConnectionsMatchSpec,
    exports.PinOffBoardConnection,
];
//# sourceMappingURL=electrical-interface-compatibility.js.map